import React, { useState, useEffect, useRef } from 'react';
import { Clock, Sparkles, Calendar, Scissors } from 'lucide-react';
import { Button } from './ui/button';
import { services, businessInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Services = () => {
  const [visibleRows, setVisibleRows] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [scanLine, setScanLine] = useState(false);
  const sectionRef = useRef(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setScanLine(true);
            services.forEach((service, index) => {
              setTimeout(() => {
                setVisibleRows((prev) => [...prev, service.id]);
              }, index * 100);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#00D9FF]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '3s' }} />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#FF1493]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '4s', animationDelay: '1.5s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-effect border border-white/20 backdrop-blur-xl">
              <Sparkles className="w-5 h-5 text-[#00D9FF] animate-pulse" />
              <span className="text-sm font-bold gradient-text tracking-widest">{t.services.badge}</span>
            </div>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-slide-in-left" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t.services.title} <span className="gradient-text">{t.services.titleAccent}</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
            {t.services.subtitle}
          </p>
        </div>

        {/* Price List Card */}
        <div className="max-w-3xl mx-auto">
          <div className="relative group/card">
            {/* Animated rotating border */}
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-conic from-[#00D9FF] via-[#FF1493] via-[#5B7FFF] to-[#00D9FF] animate-spin-slow opacity-60" 
                style={{ 
                  background: 'conic-gradient(from 0deg, #00D9FF, #FF1493, #5B7FFF, #00D9FF)',
                  animation: 'spin 6s linear infinite'
                }} 
              />
            </div>

            {/* Inner card */}
            <div className="relative bg-[#0a0a0a] rounded-2xl p-8 md:p-12 overflow-hidden">
              {/* Scan line effect */}
              {scanLine && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  <div 
                    className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00D9FF]/60 to-transparent"
                    style={{
                      animation: 'scanDown 3s ease-in-out infinite',
                      boxShadow: '0 0 20px 5px rgba(0, 217, 255, 0.3)'
                    }}
                  />
                </div>
              )}

              {/* Noise/grain texture overlay */}
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
                }}
              />

              {/* Card header with neon glow */}
              <div className="text-center mb-10 relative">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="h-[2px] w-16 bg-gradient-to-r from-transparent to-[#00D9FF]" style={{ boxShadow: '0 0 10px #00D9FF' }} />
                  <Scissors className="w-7 h-7 text-[#00D9FF] animate-pulse" style={{ filter: 'drop-shadow(0 0 8px #00D9FF)' }} />
                  <div className="h-[2px] w-16 bg-gradient-to-l from-transparent to-[#00D9FF]" style={{ boxShadow: '0 0 10px #00D9FF' }} />
                </div>
                <h3 
                  className="text-3xl md:text-4xl font-black tracking-wider"
                  style={{ 
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#00D9FF',
                    textShadow: '0 0 10px rgba(0, 217, 255, 0.5), 0 0 40px rgba(0, 217, 255, 0.2), 0 0 80px rgba(0, 217, 255, 0.1)'
                  }}
                >
                  DNA BARBERSHOP
                </h3>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <div className="h-[1px] w-8 bg-[#FF1493]/40" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FF1493] animate-pulse" style={{ boxShadow: '0 0 8px #FF1493' }} />
                  <div className="h-[1px] w-20 bg-gradient-to-r from-[#FF1493]/40 to-transparent" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00D9FF] animate-pulse" style={{ boxShadow: '0 0 8px #00D9FF', animationDelay: '0.5s' }} />
                  <div className="h-[1px] w-8 bg-[#00D9FF]/40" />
                </div>
              </div>

              {/* Price rows */}
              <div className="space-y-0">
                {services.map((service, index) => (
                  <div
                    key={service.id}
                    data-testid={`service-row-${service.id}`}
                    className={`group relative py-5 px-5 rounded-xl transition-all duration-500 cursor-default ${
                      visibleRows.includes(service.id)
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-6'
                    }`}
                    onMouseEnter={() => setHoveredRow(service.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      transitionDelay: visibleRows.includes(service.id) ? '0ms' : `${index * 100}ms`
                    }}
                  >
                    {/* Hover background glow */}
                    <div className={`absolute inset-0 rounded-xl transition-all duration-500 ${
                      hoveredRow === service.id
                        ? 'opacity-100'
                        : 'opacity-0'
                    }`}
                      style={{
                        background: 'linear-gradient(90deg, rgba(0,217,255,0.08) 0%, rgba(91,127,255,0.04) 50%, rgba(255,20,147,0.08) 100%)',
                        boxShadow: hoveredRow === service.id ? 'inset 0 0 30px rgba(0, 217, 255, 0.05)' : 'none'
                      }}
                    />

                    {/* Left neon accent bar */}
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-500 ${
                      hoveredRow === service.id ? 'h-8 opacity-100' : 'h-0 opacity-0'
                    }`}
                      style={{ 
                        background: 'linear-gradient(to bottom, #00D9FF, #FF1493)',
                        boxShadow: '0 0 12px #00D9FF, 0 0 24px rgba(0, 217, 255, 0.3)'
                      }}
                    />

                    <div className="relative flex items-center justify-between">
                      {/* Service info */}
                      <div className="flex-shrink-0 pr-4">
                        <span className={`text-base md:text-lg font-bold transition-all duration-400 block ${
                          hoveredRow === service.id
                            ? 'text-white translate-x-2'
                            : 'text-gray-300'
                        }`}
                          style={hoveredRow === service.id ? { textShadow: '0 0 20px rgba(255,255,255,0.15)' } : {}}
                        >
                          {language === 'en' && service.nameEn ? service.nameEn : service.name}
                        </span>
                        <div className={`flex items-center gap-2 mt-1 transition-all duration-400 ${
                          hoveredRow === service.id ? 'translate-x-2' : ''
                        }`}>
                          <Clock className={`w-3 h-3 transition-all duration-300 ${
                            hoveredRow === service.id ? 'text-[#00D9FF]' : 'text-gray-700'
                          }`} 
                            style={hoveredRow === service.id ? { filter: 'drop-shadow(0 0 4px #00D9FF)' } : {}}
                          />
                          <span className={`text-xs transition-colors duration-300 ${
                            hoveredRow === service.id ? 'text-gray-400' : 'text-gray-600'
                          }`}>{service.duration}</span>
                        </div>
                      </div>

                      {/* Animated connector dots */}
                      <div className="flex-1 mx-4 flex items-center gap-[6px] self-center mb-3 overflow-hidden">
                        {[...Array(40)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-[3px] h-[3px] rounded-full flex-shrink-0 transition-all ${
                              hoveredRow === service.id ? 'bg-[#00D9FF]/40' : 'bg-gray-800'
                            }`}
                            style={hoveredRow === service.id ? {
                              animationName: 'dotPulse',
                              animationDuration: '1.5s',
                              animationDelay: `${i * 30}ms`,
                              animationIterationCount: 'infinite',
                              animationTimingFunction: 'ease-in-out'
                            } : {}}
                          />
                        ))}
                      </div>

                      {/* Price with neon effect */}
                      <div 
                        className={`text-2xl md:text-3xl font-black transition-all duration-500 flex-shrink-0 ${
                          hoveredRow === service.id
                            ? 'scale-125 -translate-x-1'
                            : ''
                        }`}
                        style={{
                          color: hoveredRow === service.id ? '#00D9FF' : '#00D9FF',
                          textShadow: hoveredRow === service.id 
                            ? '0 0 10px #00D9FF, 0 0 30px rgba(0, 217, 255, 0.5), 0 0 60px rgba(0, 217, 255, 0.2)' 
                            : '0 0 5px rgba(0, 217, 255, 0.2)',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {service.price}$
                      </div>
                    </div>

                    {/* Bottom separator with glow */}
                    <div className={`absolute bottom-0 left-5 right-5 h-[1px] transition-all duration-500 ${
                      hoveredRow === service.id
                        ? 'bg-gradient-to-r from-[#00D9FF]/40 via-[#5B7FFF]/20 to-[#FF1493]/40'
                        : 'bg-white/[0.04]'
                    }`}
                      style={hoveredRow === service.id ? { boxShadow: '0 0 8px rgba(0, 217, 255, 0.15)' } : {}}
                    />
                  </div>
                ))}
              </div>

              {/* Bottom decorative element */}
              <div className="flex items-center justify-center gap-4 mt-10">
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[#00D9FF]/20 to-transparent" />
                <Scissors className="w-4 h-4 text-[#FF1493]/40 rotate-90" />
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[#FF1493]/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* CTA button */}
        <div className="text-center mt-14 animate-fade-in" style={{ animationDelay: '1s' }}>
          <a href={businessInfo.bookingUrl} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="book-appointment-btn"
              size="lg"
              className="group relative bg-gradient-to-r from-[#00D9FF] via-[#5B7FFF] to-[#FF1493] text-white hover:shadow-2xl hover:shadow-[#00D9FF]/50 font-bold px-12 py-7 text-xl transition-all duration-500 hover:scale-110 border-0 overflow-hidden rounded-xl"
            >
              <span className="relative z-10 flex items-center">
                <Calendar className="w-6 h-6 mr-3 group-hover:animate-bounce" />
                {t.services.bookAppointment}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </Button>
          </a>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes scanDown {
          0% { top: -2px; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); background-color: #00D9FF; }
        }
      `}</style>
    </section>
  );
};
